import { Stack, Switch, Text } from '@mantine/core';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { i18nKeys, tKeys, tKeysLoose } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useEffect, useState } from 'react';
import { isNullish } from 'remeda';

import { type Automation, useUpdateAutomation } from '../../../api';
import { TargetModelCell } from '../../AutomationsIndex/AutomationsTable/components';
import { InfoRow } from '../Automation';

type InformationPanelProps = {
  automation: Automation;
  isLoading: boolean;
};

export const InformationPanel = ({ automation, isLoading }: InformationPanelProps) => {
  const { t } = useTranslation();

  const { updateAutomation } = useUpdateAutomation();

  const [isActive, setIsActive] = useState(automation.active);

  useEffect(() => {
    setIsActive(automation.active);
  }, [automation.active]);

  const handleDisabledAutomation = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.currentTarget.checked;
    setIsActive(isChecked);
    updateAutomation({ id: automation.id, active: isChecked });
  };

  return (
    <Panel
      isLoading={isLoading}
      title={automation.name}
      withBorder
      topRight={
        <Switch
          checked={isActive}
          c="gray.10"
          color="purple.4"
          onChange={handleDisabledAutomation}
          size="sm"
          label={isActive ? t(i18nKeys.SETTINGS.STAFF.ACTIVE) : t(i18nKeys.SETTINGS.STAFF.INACTIVE)}
          labelPosition="left"
        />
      }
    >
      <Stack mt="space2">
        {automation.description && (
          <InfoRow label={t(tKeys.AUTOMATION.COLUMNS.DESCRIPTION)}>
            <Text c="gray.10">{automation.description}</Text>
          </InfoRow>
        )}
        <InfoRow label={t(tKeys.AUTOMATION.COLUMNS.MODEL)}>
          <TargetModelCell model={automation.targetModel} />
        </InfoRow>
        <InfoRow label={t(tKeys.AUTOMATION.TRIGGERS.LABEL)}>
          <Text c="gray.10">
            {automation.triggers
              .map(trigger =>
                t(tKeysLoose.AUTOMATION.WIZARD.STEP2[trigger.toUpperCase().replaceAll('_', '')]),
              )
              .join(', ')}
          </Text>
        </InfoRow>
        <InfoRow label={t(tKeys.AUTOMATION.COLUMNS.LAST_RUN_AT)}>
          <Text c="gray.10">
            {isNullish(automation.lastActivityAt)
              ? '-'
              : dayjs(automation.lastActivityAt).format('LLL')}
          </Text>
        </InfoRow>
      </Stack>
    </Panel>
  );
};
